import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject, timer } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { DownloadedExportsService } from '@shared/components/files/shared/downloaded-exports.service';
import { DocumentExportJob } from '@shared/components/files/shared/models/document-export-job.model';
import { DocumentExportJobStatus } from '@shared/components/files/shared/models/document-export-job-status.enum';
import { EventService } from '@shared/event.service';

/**
 * In-app panel that lists the current user's recent export jobs. Polls
 * {@code GET /document-exports} every 5 seconds while open, so status transitions from the
 * scheduler and worker surface without a manual refresh.
 *
 * <p>Primary responsibilities:
 * <ul>
 *   <li>List non-deleted jobs for the current user (backend caps at 50 rows).</li>
 *   <li>Allow the user to initiate a browser download from a {@code COMPLETED} row.</li>
 *   <li>Allow the user to soft-delete any job (confirm first for in-flight jobs, because
 *       that cancels work they may not realize is still running).</li>
 * </ul>
 *
 * <p>The parent toggles this component via {@code *ngIf}; the component relies on that
 * destroy/recreate cycle to stop its polling, so there is no manual cleanup in the parent.
 */
@Component({
  selector: 'oes-current-exports-panel',
  templateUrl: './current-exports-panel.component.html',
  styleUrls: ['./current-exports-panel.component.scss'],
})
export class CurrentExportsPanelComponent implements OnInit, OnDestroy {

  @Input() organizationId: string;
  @Output() closed = new EventEmitter<void>();

  readonly STATUS = DocumentExportJobStatus;
  readonly REFRESH_INTERVAL_MS = 5000;
  readonly LIST_CAP = 50;

  jobs: DocumentExportJob[] = [];
  loading = true;
  errorMessage: string | null = null;

  private readonly destroyed$ = new Subject<void>();
  private readonly stopPolling$ = new Subject<void>();
  private readonly activeDownloads = new Set<string>();

  constructor(
    private documentDataService: DocumentDataService,
    private downloadedExportsService: DownloadedExportsService,
    private eventService: EventService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.loading = false;
      this.errorMessage = this.translateService.instant('files.current-exports.load-failed');
      return;
    }
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.stopPolling$.complete();
  }

  /**
   * Tear down any existing poll and start a new one. Called by the parent when a new export
   * is submitted while the panel is open, so the just-created row appears immediately instead
   * of waiting for the next background refresh. Safe to call whether polling is active or
   * already stopped (e.g., all prior jobs had reached terminal state).
   */
  refresh(): void {
    if (!this.organizationId) {
      return;
    }
    this.stopPolling$.next();
    this.startPolling();
  }

  /**
   * Start (or restart) the background refresh. Polls at {@link REFRESH_INTERVAL_MS} until
   * either the component is destroyed or every listed job has reached a terminal state
   * (COMPLETED, FAILED, or EXPIRED). Once all jobs are terminal the poll stops — there's
   * nothing left to observe and new jobs will appear when the user reopens the panel.
   */
  private startPolling(): void {
    timer(0, this.REFRESH_INTERVAL_MS)
      .pipe(
        switchMap(() => this.documentDataService.listExportJobs(this.organizationId)),
        takeUntil(merge(this.destroyed$, this.stopPolling$)),
      )
      .subscribe({
        next: (jobs) => {
          this.jobs = jobs || [];
          this.loading = false;
          this.errorMessage = null;
          // Drop stored "downloaded" markers for jobs that are no longer in the list (expired,
          // soft-deleted, or aged out). Keeps localStorage tidy.
          this.downloadedExportsService.pruneToLiveJobs(this.jobs.map((j) => j.jobId));
          if (!this.hasInFlightJobs()) {
            // No job in QUEUED or RUNNING — nothing will change until the user submits a
            // new one, so stop the 5s heartbeat.
            this.stopPolling$.next();
          }
        },
        error: () => {
          this.loading = false;
          this.errorMessage = this.translateService.instant('files.current-exports.load-failed');
        },
      });
  }

  isDownloaded(jobId: string): boolean {
    return this.downloadedExportsService.isDownloaded(jobId);
  }

  isDownloading(jobId: string): boolean {
    return this.activeDownloads.has(jobId);
  }

  private hasInFlightJobs(): boolean {
    return this.jobs.some(
      (job) =>
        job.status === DocumentExportJobStatus.QUEUED ||
        job.status === DocumentExportJobStatus.RUNNING,
    );
  }

  close(): void {
    this.closed.emit();
  }

  download(job: DocumentExportJob): void {
    if (job.status !== DocumentExportJobStatus.COMPLETED) {
      return;
    }
    if (this.activeDownloads.has(job.jobId)) {
      // Guard against double-click while the iframe handoff is still in flight.
      return;
    }
    this.activeDownloads.add(job.jobId);
    this.documentDataService
      .downloadExportJob(this.organizationId, job.jobId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.activeDownloads.delete(job.jobId);
          this.downloadedExportsService.markDownloaded(job.jobId);
        },
        error: (error: Error) => {
          this.activeDownloads.delete(job.jobId);
          this.eventService.error(
            error?.message ||
              this.translateService.instant('files.current-exports.download-failed'),
          );
        },
      });
  }

  remove(job: DocumentExportJob): void {
    // Confirm before removing an in-flight job — that stops (from the user's point of view)
    // work they may not realize is still running.
    if (
      job.status === DocumentExportJobStatus.QUEUED ||
      job.status === DocumentExportJobStatus.RUNNING
    ) {
      const confirmed = window.confirm(
        this.translateService.instant('files.current-exports.confirm-remove-running'),
      );
      if (!confirmed) {
        return;
      }
    }

    // Optimistic removal for snappy UX; rollback if the backend call fails.
    const previousJobs = this.jobs;
    this.jobs = this.jobs.filter((j) => j.jobId !== job.jobId);

    this.documentDataService
      .deleteExportJob(this.organizationId, job.jobId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Removal persisted; the next poll tick will reconcile in any case.
        },
        error: () => {
          this.jobs = previousJobs;
          this.eventService.error(
            this.translateService.instant('files.current-exports.remove-failed'),
          );
        },
      });
  }

  humanReadableSize(bytes: number): string {
    if (!bytes) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const precision = unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  }

  statusLabelKey(status: DocumentExportJobStatus): string {
    switch (status) {
      case DocumentExportJobStatus.QUEUED:
        return 'files.current-exports.status-queued';
      case DocumentExportJobStatus.RUNNING:
        return 'files.current-exports.status-running';
      case DocumentExportJobStatus.COMPLETED:
        return 'files.current-exports.status-completed';
      case DocumentExportJobStatus.FAILED:
        return 'files.current-exports.status-failed';
      case DocumentExportJobStatus.EXPIRED:
        return 'files.current-exports.status-expired';
      default:
        return '';
    }
  }

  trackByJobId(_index: number, job: DocumentExportJob): string {
    return job.jobId;
  }
}
